import { useSlate } from 'slate-react';
import { SectionEntry, SectionTargetLanguage } from 'entities/document';
import { useLocation } from '@tanstack/react-router';
import { Range, Editor, Element } from 'slate';
import { forwardRef } from 'react';
import { LinkElement } from 'slate/types/slate';
import { insertLink, updateLinkBookmark } from 'slate/extensions/link';
import { useBookmarks } from 'entities/bookmark';
import { v4 as uuidv4 } from 'uuid';

import { groupBy } from 'lodash';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

interface LinkButtonProps {
  icon: string;
  css?: string;
  format?: string;
  text?: string;
}

type BookmarkItem = NonNullable<ReturnType<typeof useBookmarks>['bookmarks']>[number];
type GroupedBookmark = BookmarkItem & { sectionName?: string };

export const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(({ icon, css }, ref) => {
  const editor = useSlate();
  const { selection } = editor;
  const hasSelectedText = selection && !Range.isCollapsed(selection);
  return (
    <button
      ref={ref}
      className={`${css}`}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      disabled={!hasSelectedText}
    >
      <i className={`fa-regular fa-${icon}`} />
    </button>
  );
});

export const LinkContent = forwardRef<HTMLDivElement>((_, ref) => {
  const editor = useSlate();
  const { documentSuperId, superId: sectionId } = useEditorLiveSection();
  const { createLink, bookmarks, isBookmarksLoading, updateLink } = useBookmarks(documentSuperId);
  const { data: pDocument } = useEditorDocument();
  const content = pDocument?.content;
  const location = useLocation();

  const queryParams = new URLSearchParams(location.searchStr);
  const translationId = queryParams.get('translation');
  const groupedBookmarks = groupBy(
    bookmarks
      ?.map((bookmark): GroupedBookmark | undefined => {
        const section = translationId
          ? content?.sections.find((section: SectionEntry) =>
              section.targetLanguages?.some((targetSection: SectionTargetLanguage) => targetSection.superId === bookmark.sectionId)
            )
          : content?.sections.find((section: SectionEntry) => section.superId === bookmark.sectionId);

        if (section) {
          return {
            ...bookmark,
            sectionName: section.name,
          };
        }
        return undefined;
      })
      .filter((bookmark): bookmark is GroupedBookmark => Boolean(bookmark)) || [],
    'sectionName'
  );

  const handleSubmit = (bookmarkId: string) => {
    const linkId = `lk-${uuidv4()}`;
    const { selection } = editor;
    if (!selection || Range.isCollapsed(selection)) return;
    const [linkNode] = Editor.nodes<LinkElement>(editor, {
      match: (n): n is LinkElement => Element.isElement(n) && n.type === 'link',
    });
    if (!!linkNode) {
      const existingLinkId = linkNode[0].id as string;
      updateLink({ id: existingLinkId, bookmarkId });
      updateLinkBookmark(editor, existingLinkId, bookmarkId);
    } else {
      const label = Editor.string(editor, selection);
      createLink({ linkId, documentSuperId, sectionId, label, bookmarkId });
      insertLink(editor, linkId, bookmarkId, String(documentSuperId), sectionId);
    }
  };
  if (isBookmarksLoading) return <div>Loading...</div>;
  return (
    <div ref={ref} className={`bookmarkbar-content`}>
      {groupedBookmarks &&
        Object.keys(groupedBookmarks).map((sectionName) => (
          <div key={sectionName}>
            <h4>{sectionName}</h4>
            <div className="divider horizontal" />
            {groupedBookmarks[sectionName].map((b) => (
              <button key={b.bookmarkId} onClick={() => handleSubmit(b.bookmarkId)} className="link-button">
                {`Label: ${b.label}`}
              </button>
            ))}
          </div>
        ))}
    </div>
  );
});
